import React from 'react';
import type { Transaction } from '../types';

interface TransactionItemProps {
  transaction: Transaction;
}

// 平台显示名称和颜色
const platformConfig: Record<string, { name: string; color: string }> = {
  ebay: { name: 'eBay', color: 'bg-blue-100 text-blue-700' },
  snkrdunk: { name: 'SNKRDUNK', color: 'bg-gray-100 text-gray-700' },
  煤炉: { name: '煤炉', color: 'bg-orange-100 text-orange-700' },
};

export default function TransactionItem({ transaction }: TransactionItemProps) {
  const platformInfo = platformConfig[transaction.platform] || {
    name: transaction.platform,
    color: 'bg-gray-100 text-gray-600',
  };

  const formatDate = (timestamp: number) => {
    // 服务器返回 13 位毫秒级时间戳，直接使用
    const date = new Date(timestamp);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const formatPrice = (price: number, currency: string) => {
    const currencySymbols: Record<string, string> = {
      USD: '$',
      JPY: '¥',
      CNY: '¥',
      EUR: '€',
    };
    const symbol = currencySymbols[currency] || currency;
    return `${symbol}${price.toLocaleString()}`;
  };

  return (
    <div className="bg-white border border-border rounded-lg p-4 hover:shadow-sm transition-shadow">
      <div className="flex gap-4">
        {/* 卡片图片 */}
        <div className="w-20 aspect-[708/1188] flex-shrink-0 bg-background-secondary rounded overflow-hidden">
          {transaction.soldImage ? (
            <img
              src={transaction.soldImage}
              alt={transaction.description}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-secondary-light">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}
        </div>

        {/* 交易信息 */}
        <div className="flex-1 min-w-0">
          {/* 描述 */}
          <h4 className="text-sm font-medium text-primary line-clamp-2">
            {transaction.description}
          </h4>

          {/* 价格 */}
          <p className="text-lg font-semibold text-primary mt-2">
            {formatPrice(transaction.price, transaction.currency)}
          </p>

          {/* 平台和时间 */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full ${platformInfo.color}`}>
              {platformInfo.name}
            </span>
            <span className="text-xs text-secondary-light">
              {formatDate(transaction.soldTime)}
            </span>
          </div>

          {/* 评级信息 */}
          {transaction.grade.agency && transaction.grade.score && (
            <div className="mt-2">
              <span className="text-xs px-2 py-0.5 bg-yellow-50 text-yellow-700 rounded">
                {transaction.grade.agency} {transaction.grade.score}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
